import { useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { v4 as uuid } from 'uuid';

/* Styled Components */
import {
  FormSection,
  PageSubtitle,
  PageFooter,
  SelectInputWrapper,
} from 'styles/signup/MultiScreenSignupPage';

/* Child Components */
import Button, { BUTTON_COLOR_KEYS } from 'components/common/Button';
import SectionHeader from 'components/signup/multiscreen/SectionHeader';
import PlaceAutocompleteTextInput from 'components/signup/autocomplete/PlaceAutocompleteTextInput';
import WishSelectInput from 'components/common/WishSelectInput';
import WishTextInput from 'components/common/WishTextInput';

/* Actions */
import {
  submitSignupStep as submitSignupStepAction,
  setCurrentSignupStep as setCurrentSignupStepAction,
  setStoreAddressLine1 as setStoreAddressLine1Action,
  setStoreAddressLine2 as setStoreAddressLine2Action,
  setCity as setCityAction,
  setZipCode as setZipCodeAction,
  setState as setStateAction,
  setCountry as setCountryAction,
  setCountryCode as setCountryCodeAction,
  setPhoneNumber as setPhoneNumberAction,
  setAutocompleteSessionToken as setAutocompleteSessionTokenAction,
  setAutocompleteAttributions as setAutocompleteAttributionsAction,
  setJustDidAutoComplete as setJustDidAutoCompleteAction,
  setPlaceDetailsResult as setPlaceDetailsResultAction,
  setErrorMessage as setErrorMessageAction,
  clearErrorMessage as clearErrorAction,
} from 'app/signupSlice';

/* Selectors */
import { getLocale } from 'app/appSlice';
import {
  getStoreAddressLine1,
  getStoreAddressLine2,
  getCity,
  getZipCode,
  getState,
  getCountry,
  getCountryCode as getSignupCountryCode,
  getPhoneNumber,
  getAutocompleteSessionToken,
  getAutocompleteAttributions,
  getJustDidAutoComplete,
  getPlaceDetailsResult,
  getErrorMessage,
} from 'app/signupSlice';

/* Utils */
import { i18n } from 'utils/i18n';
import {
  countryCodetoCountry,
  getStatesForCountry,
  autocompleteLatamAddressLine1,
} from 'utils/AutocompleteComponents';
import { validatePhoneNumber } from 'utils/PhoneNumber';

/* Constants */
import {
  SIGNUP_SCREEN,
  ADDRESS_FORMAT,
  AUTOCOMPLETE_DETAILS_FIELDS,
  LATAM_ADDRESS_COUNTRIES,
} from 'constants/SignupConstants';
import {
  getCountryStateMapping,
  getCountryNames,
  getCountryNameToCode,
} from 'constants/SignupFormConstants';

/* Type */
import type { AppState, AppDispatch } from 'app/store';
import type { OptionsType as SubmitSignupStepOptionsType } from 'services/SubmitSignupStepService';
import type {
  PlaceDetailsResultType,
  PlaceDetailsAddressComponents,
} from 'types/GooglePlacesTypes';

const mapStateToProps = (state: AppState) => ({
  locale: getLocale(state),
  storeAddressLine1: getStoreAddressLine1(state),
  storeAddressLine2: getStoreAddressLine2(state),
  city: getCity(state),
  zipCode: getZipCode(state),
  state: getState(state),
  country: getCountry(state),
  countryCode: getSignupCountryCode(state),
  phoneNumber: getPhoneNumber(state),
  autocompleteSessionToken: getAutocompleteSessionToken(state),
  autocompleteAttributions: getAutocompleteAttributions(state),
  justDidAutoComplete: getJustDidAutoComplete(state),
  placeDetailsResult: getPlaceDetailsResult(state),
  errorMessage: getErrorMessage(state),
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  setStoreAddressLine1: (storeAddressLine1: string) =>
    dispatch(setStoreAddressLine1Action(storeAddressLine1)),
  setStoreAddressLine2: (storeAddressLine2: string) =>
    dispatch(setStoreAddressLine2Action(storeAddressLine2)),
  setCity: (city: string) => dispatch(setCityAction(city)),
  setZipCode: (zipCode: string) => dispatch(setZipCodeAction(zipCode)),
  setState: (state: string) => dispatch(setStateAction(state)),
  setCountry: (country: string) => dispatch(setCountryAction(country)),
  setCountryCode: (country: string) => dispatch(setCountryCodeAction(country)),
  setPhoneNumber: (phoneNumber: string) =>
    dispatch(setPhoneNumberAction(phoneNumber)),
  setAutocompleteSessionToken: (autocompleteSessionToken: string) =>
    dispatch(setAutocompleteSessionTokenAction(autocompleteSessionToken)),
  setAutocompleteAttributions: (autocompleteAttributions: Array<string>) =>
    dispatch(setAutocompleteAttributionsAction(autocompleteAttributions)),
  setJustDidAutoComplete: (justDidAutoComplete: boolean) =>
    dispatch(setJustDidAutoCompleteAction(justDidAutoComplete)),
  setPlaceDetailsResult: (
    placeDetailsResult: PlaceDetailsResultType | undefined,
  ) => dispatch(setPlaceDetailsResultAction(placeDetailsResult)),
  submitSignupStep: (args: {
    signupStep: Values<typeof SIGNUP_SCREEN>;
    nextSignupStep: Values<typeof SIGNUP_SCREEN>;
    options: SubmitSignupStepOptionsType;
  }) => dispatch(submitSignupStepAction(args)),
  setCurrentSignupStep: (signupStep: Values<typeof SIGNUP_SCREEN>) =>
    dispatch(setCurrentSignupStepAction(signupStep)),
  setErrorMessage: (errorMessage: string) =>
    dispatch(setErrorMessageAction(errorMessage)),
  clearError: () => dispatch(clearErrorAction()),
});

type PropsType = ReturnType<typeof mapStateToProps> &
  ReturnType<typeof mapDispatchToProps>;

const StoreAddressPage = ({
  submitSignupStep,
  setCurrentSignupStep,
  errorMessage,
  setErrorMessage,
  clearError,
  locale,
  storeAddressLine1,
  storeAddressLine2,
  city,
  zipCode,
  state,
  country,
  countryCode,
  phoneNumber,
  autocompleteSessionToken,
  autocompleteAttributions,
  justDidAutoComplete,
  placeDetailsResult,
  setStoreAddressLine1,
  setStoreAddressLine2,
  setCity,
  setZipCode,
  setState,
  setCountry,
  setCountryCode,
  setPhoneNumber,
  setAutocompleteSessionToken,
  setAutocompleteAttributions,
  setJustDidAutoComplete,
  setPlaceDetailsResult,
}: PropsType) => {
  const addressWasAutocompleted = useRef<boolean>(Boolean(placeDetailsResult));
  const [selectedCountryIndex, setSelectedCountryIndex] = useState<number>();
  const [selectedStateIndex, setSelectedStateIndex] = useState<number>();
  const [shouldShowList, setShouldShowList] = useState<boolean>(
    !addressWasAutocompleted.current,
  );

  const statesByCountry = getCountryStateMapping();
  const countries = getCountryNames();
  const countryNameToCountryCode = getCountryNameToCode();

  const resetAutocompleteSessionToken = () => {
    setAutocompleteSessionToken(uuid());
  };

  const resetAutofillIfReqd = () => {
    // if user clicks on autocomplete options, this should not reset anything the first time
    if (justDidAutoComplete) {
      setJustDidAutoComplete(false);
      return;
    }

    // if we came into this function as a result of something other than autocomplete,
    // we reset autocomplete vars
    if (
      placeDetailsResult?.place_id ||
      autocompleteAttributions?.length !== 0
    ) {
      setPlaceDetailsResult(undefined);
    }
  };

  const onChangeCountry = (selectedIndex: number) => {
    clearError();
    resetAutofillIfReqd();
    if (countries) {
      const selectedCountry = countries[selectedIndex];
      setCountry(selectedCountry);
      setSelectedCountryIndex(selectedIndex);
      setState('');
      setSelectedStateIndex(undefined);
      setCountryCode(countryNameToCountryCode[selectedCountry]);
    }
  };

  const onChangeStateDropdown = (selectedIndex: number) => {
    clearError();
    resetAutofillIfReqd();
    if (
      selectedCountryIndex !== null &&
      selectedCountryIndex !== undefined &&
      countries
    ) {
      const currentStates = getStatesForCountry(
        countries[selectedCountryIndex],
        countryNameToCountryCode,
        statesByCountry,
      );
      if (currentStates) {
        setSelectedStateIndex(selectedIndex);
        setState(currentStates[selectedIndex]);
      }
    }
  };

  const autocompleteAddressComponents = (
    addressComponents: PlaceDetailsAddressComponents,
  ) => {
    const findShortNameOfType = (componentType: string) =>
      addressComponents.find(({ types }) => types.includes(componentType))
        ?.short_name;

    const findFullNameOfType = (componentType: string) =>
      addressComponents.find(({ types }) => types.includes(componentType))
        ?.long_name;

    // set Country
    // eslint-disable-next-line no-shadow
    const countryCode = findShortNameOfType('country');
    const fetchedCountry = countryCodetoCountry(
      countryCode,
      countryNameToCountryCode,
    );

    if (fetchedCountry && countries?.includes(fetchedCountry)) {
      onChangeCountry(countries.indexOf(fetchedCountry));
    }

    // set State/Province
    const newCountry = fetchedCountry;
    const fetchedState = findFullNameOfType('administrative_area_level_1');
    if (fetchedState) {
      const currentStates = getStatesForCountry(
        newCountry,
        countryNameToCountryCode,
        statesByCountry,
      );
      if (!currentStates || currentStates.includes(fetchedState)) {
        setState(fetchedState);
        setSelectedStateIndex(currentStates?.indexOf(fetchedState));
      }
    }

    // formatted LATAM country
    if (
      countryCode &&
      Object.prototype.hasOwnProperty.call(ADDRESS_FORMAT, countryCode)
    ) {
      const addressConfig = ADDRESS_FORMAT[countryCode];
      for (const component of Object.keys(addressConfig)) {
        const config = addressConfig[component];
        const data: string[] = config.order.reduce(
          (accumulatedData: string[], typeList: string[]) => {
            const dataList: string[] = typeList.reduce(
              (accumulatedDataList: string[], type: string) => {
                const typeData = config.fullName
                  ? findFullNameOfType(type)
                  : findShortNameOfType(type);
                return typeData
                  ? [...accumulatedDataList, typeData]
                  : accumulatedDataList;
              },
              [],
            );
            const dataString = dataList.join(' ');
            return dataString
              ? [...accumulatedData, dataString]
              : accumulatedData;
          },
          [],
        );
        const addressString = data.join(', ');
        switch (component) {
          case 'storeAddressLine1':
            setStoreAddressLine1(addressString);
            break;
          case 'storeAddressLine2':
            setStoreAddressLine2(addressString);
            break;
          case 'city':
            setCity(addressString);
            break;
          case 'zipCode':
            setZipCode(addressString);
            break;
          default:
            break;
        }
      }
      return;
    }

    const floor = findFullNameOfType('floor');
    const room = findFullNameOfType('room');
    const subpremise = findFullNameOfType('subpremise');
    const addressLine2Components = [
      floor && i18n('Floor %1$s', floor),
      room && i18n('Room %1$s', room),
      subpremise,
    ].filter(component => component);
    const fetchedStoreAddressLine2 = addressLine2Components.join(', ');

    if (fetchedStoreAddressLine2) {
      setStoreAddressLine2(fetchedStoreAddressLine2);
    }

    const fetchedCity =
      findFullNameOfType('postal_town') || findFullNameOfType('locality');
    if (fetchedCity) {
      setCity(fetchedCity);
    }

    const streetAddress = findFullNameOfType('street_address');
    const streetNumber = findFullNameOfType('street_number');
    const route = findFullNameOfType('route');
    const neighbourhood =
      findFullNameOfType('neighbourhood') ||
      findFullNameOfType('sublocality_level_1');

    let fetchedStoreAddressLine1;
    if (countryCode && LATAM_ADDRESS_COUNTRIES.has(countryCode)) {
      fetchedStoreAddressLine1 = autocompleteLatamAddressLine1(
        countryCode,
        streetAddress,
        streetNumber,
        route,
        neighbourhood,
      );
    } else {
      fetchedStoreAddressLine1 =
        streetAddress || (streetNumber && route && `${streetNumber} ${route}`);
    }

    if (fetchedStoreAddressLine1) {
      setStoreAddressLine1(fetchedStoreAddressLine1);
    }

    const fetchedZipCode = findFullNameOfType('postal_code');
    if (fetchedZipCode) {
      setZipCode(fetchedZipCode);
    }
  };

  const onAutocompleteDetails = (
    result: PlaceDetailsResultType,
    attributions: Array<string>,
  ) => {
    setAutocompleteAttributions(attributions);
    setPlaceDetailsResult(result);
    setJustDidAutoComplete(true);

    const {
      address_components: addressComponents,
      formatted_phone_number: fetchedPhoneNumber,
    } = result;

    if (addressComponents) {
      autocompleteAddressComponents(addressComponents);
    }

    if (fetchedPhoneNumber) {
      setPhoneNumber(fetchedPhoneNumber);
    }
  };

  const onInputChange = (event: React.SyntheticEvent<HTMLInputElement>) => {
    clearError();
    const { value } = event.currentTarget;
    const { id } = event.currentTarget.dataset;
    switch (id) {
      case 'storeAddressLine1':
        setShouldShowList(true);
        setStoreAddressLine1(value);
        break;
      case 'storeAddressLine2':
        setStoreAddressLine2(value);
        break;
      case 'city':
        setCity(value);
        break;
      case 'zipCode':
        setZipCode(value);
        break;
      case 'state':
        setState(value);
        break;
      case 'phoneNumber':
        setPhoneNumber(value);
        break;
      default:
        break;
    }

    const resetFields = [
      'city',
      'zipCode',
      'storeAddressLine1',
      'storeAddressLine2',
    ];

    if (id && resetFields.includes(id)) {
      resetAutofillIfReqd();
    }
  };
  const getCurrentSelectedCountryCode = () => {
    if (
      selectedCountryIndex !== null &&
      selectedCountryIndex !== undefined &&
      countryNameToCountryCode &&
      countries
    ) {
      return countryNameToCountryCode[countries[selectedCountryIndex]];
    }
    return undefined;
  };

  useEffect(() => {
    const loadLocationData = async () => {
      const selectedCountry =
        country || countryCodetoCountry(countryCode, countryNameToCountryCode);
      const countryIndex = countries.indexOf(selectedCountry);
      setSelectedCountryIndex(countryIndex);
      const currentStates = getStatesForCountry(
        country,
        countryNameToCountryCode,
        statesByCountry,
      );
      if (!placeDetailsResult?.address_components) {
        setCountry(selectedCountry);
        setCountryCode(countryCode);
        if (!currentStates || currentStates.includes(state)) {
          setState(state);
          setSelectedStateIndex(currentStates?.indexOf(state));
        }
      }
    };
    loadLocationData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (
      statesByCountry &&
      countries &&
      countryNameToCountryCode &&
      placeDetailsResult
    ) {
      onAutocompleteDetails(placeDetailsResult, autocompleteAttributions);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placeDetailsResult, autocompleteAttributions]);

  const trimmedStoreAddressLine1 = storeAddressLine1?.trim();
  const trimmedCity = city?.trim();
  const trimmedZipCode = zipCode?.trim();
  const trimmedCountry = country?.trim();
  const trimmedState = state?.trim();
  const trimmedPhoneNumber = phoneNumber?.trim();
  const isValidPhoneNumber = validatePhoneNumber(
    trimmedPhoneNumber || '',
    getCurrentSelectedCountryCode() as string,
  );
  const isFormComplete =
    Boolean(trimmedStoreAddressLine1) &&
    Boolean(trimmedCity) &&
    Boolean(trimmedZipCode) &&
    Boolean(trimmedCountry) &&
    ((selectedCountryIndex !== null &&
      selectedCountryIndex !== undefined &&
      countryNameToCountryCode &&
      countries &&
      statesByCountry &&
      !statesByCountry[
        countryNameToCountryCode[countries[selectedCountryIndex]]
      ]) ||
      Boolean(trimmedState)) &&
    Boolean(trimmedPhoneNumber) &&
    Boolean(isValidPhoneNumber);

  const showError = Boolean(errorMessage);

  const onSubmit = () => {
    if (!isFormComplete) {
      setErrorMessage(i18n('Complete required fields'));
      return;
    }
    submitSignupStep({
      signupStep: SIGNUP_SCREEN.STORE_ADDRESS,
      nextSignupStep: SIGNUP_SCREEN.STORE_HOURS,
      options: {
        storeAddressLine1,
        storeAddressLine2,
        city,
        zipCode,
        state,
        country,
        countryCode,
        phoneNumber,
      },
    });
  };

  let stateField: React.ReactNode = null;
  if (
    selectedCountryIndex !== null &&
    selectedCountryIndex !== undefined &&
    countryNameToCountryCode &&
    countries &&
    statesByCountry &&
    statesByCountry[countryNameToCountryCode[countries[selectedCountryIndex]]]
  ) {
    stateField = (
      <SelectInputWrapper withTopMargin withLeftMargin>
        <WishSelectInput
          title={i18n('State / Province')}
          items={
            statesByCountry[
              countryNameToCountryCode[countries[selectedCountryIndex]]
            ]
          }
          onChange={onChangeStateDropdown}
          selectedIndex={selectedStateIndex}
          styles={{ height: '40px', margin: '0' }}
          dropdownItemStyle={{
            justifyContent: 'left',
            padding: '0 12px',
          }}
          isError={!selectedStateIndex && showError}
        />
      </SelectInputWrapper>
    );
  } else {
    stateField = (
      <WishTextInput
        value={state}
        title={i18n('State / Province')}
        stretch
        margin="16px 0 0 16px"
        data-id="state"
        onChange={onInputChange}
        errorMessage={i18n('Required')}
        isErrorShown={!trimmedState && showError}
      />
    );
  }
  return (
    <>
      <SectionHeader
        title={
          addressWasAutocompleted.current
            ? i18n('Confirm your store information')
            : i18n('Enter your store information')
        }
        onBackClicked={() => {
          clearError();
          setCurrentSignupStep(SIGNUP_SCREEN.STORE_NAME);
        }}
      />
      {addressWasAutocompleted.current && (
        <PageSubtitle center>
          {i18n('Please make sure these details are correct before continuing')}
        </PageSubtitle>
      )}
      <FormSection stretch>
        <PlaceAutocompleteTextInput
          value={storeAddressLine1}
          title={i18n('Store address line 1')}
          stretch
          margin="16px 0 0 0"
          data-id="storeAddressLine1"
          onChange={onInputChange}
          detailsFields={AUTOCOMPLETE_DETAILS_FIELDS}
          country={getCurrentSelectedCountryCode()?.toLowerCase()}
          language={locale}
          types={['address']}
          sessionToken={autocompleteSessionToken}
          resetSessionToken={resetAutocompleteSessionToken}
          onDetails={onAutocompleteDetails}
          errorMessage={i18n('Required')}
          isErrorShown={!trimmedStoreAddressLine1 && showError}
          shouldShowList={shouldShowList}
        />
        <WishTextInput
          value={storeAddressLine2}
          title={i18n('Store address line 2')}
          placeholder={i18n('Optional')}
          stretch
          margin="16px 0 0 0"
          data-id="storeAddressLine2"
          onChange={onInputChange}
        />
        <FormSection flex>
          <WishTextInput
            value={city}
            title={i18n('City')}
            stretch
            margin="16px 0 0 0"
            data-id="city"
            onChange={onInputChange}
            errorMessage={i18n('Required')}
            isErrorShown={!trimmedCity && showError}
          />
          {stateField}
        </FormSection>
        <FormSection flex>
          <SelectInputWrapper withTopMargin withRightMargin>
            <WishSelectInput
              title={i18n('Country / Region')}
              items={countries}
              onChange={onChangeCountry}
              selectedIndex={selectedCountryIndex}
              styles={{ height: '40px', width: '100%', margin: '0 0 0 0' }}
              dropdownItemStyle={{
                justifyContent: 'left',
                padding: '0 12px',
              }}
              isError={!selectedCountryIndex && showError}
            />
          </SelectInputWrapper>
          <WishTextInput
            value={zipCode}
            title={i18n('Zip / Postal code')}
            stretch
            margin="16px 0 0 0"
            data-id="zipCode"
            onChange={onInputChange}
            errorMessage={i18n('Required')}
            isErrorShown={!trimmedZipCode && showError}
          />
        </FormSection>
        <WishTextInput
          value={phoneNumber}
          title={i18n('Store phone number')}
          stretch
          inputMode="numeric"
          type="tel"
          margin="16px 0 0 0"
          data-id="phoneNumber"
          onChange={onInputChange}
          errorMessage={
            trimmedPhoneNumber && !isValidPhoneNumber
              ? i18n('Please enter a valid phone number')
              : i18n('Required')
          }
          isErrorShown={
            (!trimmedPhoneNumber || !isValidPhoneNumber) && showError
          }
        />
      </FormSection>
      <PageFooter>
        <Button
          onClick={onSubmit}
          color={BUTTON_COLOR_KEYS.DARK_COBALT}
          stretch
          bold
          overrideStyle={{
            margin: '24px 0',
          }}
        >
          {i18n('Continue')}
        </Button>
      </PageFooter>
    </>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(StoreAddressPage);
